import { useState, useMemo, useEffect } from "react";
import {
  Plus, Filter, X, Search, MapPin, MessageCircle, Send, Eye, FileText,
  Phone, Ban, RotateCcw, CheckCircle2, Paperclip, CalendarClock, AlertCircle, ClipboardCheck, Zap, ArrowRight, Trash2, ShieldCheck,
} from "lucide-react";
import GuideBanner from "../components/GuideBanner";
import PainelMeta from "../components/PainelMeta";
import Badge from "../components/Badge";
import Pagination from "../components/Pagination";
import ConfirmDialog from "../components/ConfirmDialog";
import { removeFromStorage } from "../lib/storage";
import { sincronizarCadastro, sincronizarAceite } from "../lib/manutencaoSync";
import { PARTNER_STAGES, PARTNER_TYPES, BRAZILIAN_STATES, acoesDoEstagio, fichaConfirmada } from "../constants";
import { unificarRede, duplicatasDaFicha, escolherPrincipal, mesclarFichas, parceiroExistenteDaFicha } from "../lib/rede";
import {
  precisaJustificar, justificativaVigente, diasParado, rotuloMotivo,
  ultimoComprovante, PRAZO_ABORDAGEM_DIAS,
} from "../lib/abordagem";
import { pendentesDeConferencia, correcaoAberta, resumoQualidade } from "../lib/conferencia";
import { atosDoGerente, aplicarAtoGerente, evidencia } from "../lib/funil";
import { agendar, concluirDe, TITULO_RETOMADA } from "../lib/followup";
import { credStatus, waLink, getToday, addMonths, exportCSV, fmtShort, generateId, fetchCNPJ } from "../utils";

// ═══════════════════════════════════════════════════════════════
// REDE DE PARCEIROS — uma aba só
// ═══════════════════════════════════════════════════════════════
//
// "Parceiros (Rede)" e "Fichas / Credenciamento" eram duas listas da MESMA
// empresa. Quem estava na rua abria uma para ver o estágio e a outra para ver
// o documento, e as duas discordavam entre si: parceiro em "abordagem" com
// ficha concluída, ficha pendente de empresa que já estava ativa. Aqui é um
// card por empresa, com o estágio e a ficha no mesmo lugar.
//
// A junção é por `partnerId` e, quando não há, por CNPJ — que é o que os dois
// lados sempre têm. Empresa que entrou só pelo link (ficha sem parceiro)
// aparece do mesmo jeito, e o estágio dela sai do estado da ficha.

const stateLabel = (uf) => BRAZILIAN_STATES.find((x) => x.uf === uf)?.uf || uf || "—";

const GRUPOS = [
  { id: "all", label: "Todas", cor: "#7c3aed", filtro: () => true },
  { id: "novos", label: "A contatar", cor: "#94a3b8", filtro: (i) => i.estagio === "identificado" },
  // Em conversa inclui quem ACEITOU mas ainda não mandou a ficha: o trabalho
  // ali ainda é conversa — cobrar o envio do link —, não conferir documento.
  { id: "andamento", label: "Em conversa", cor: "#6366f1", filtro: (i) => i.estagio === "abordagem" || i.aguardandoFicha },
  // O grupo que existe justamente para não deixar ninguém encostado em
  // "abordagem": quem passou de 2 dias sem contato e sem justificativa.
  { id: "parados", label: `Parados +${PRAZO_ABORDAGEM_DIAS} dias`, cor: "#dc2626", filtro: (i) => precisaJustificar(i.parceiro) },
  // Só "documentacao": a empresa aceitou e está preenchendo/assinando a ficha.
  // Validação e ativação saíram daqui para não contar a mesma ficha em dois
  // grupos — elas moram em "A vincular".
  // Só quem já MANDOU a ficha (pelo link, ou assinada): é a regra do negócio —
  // "em documentação são apenas fornecedores com link e ficha enviados".
  { id: "documentacao", label: "Em documentação", cor: "#f59e0b", filtro: (i) => i.estagio === "documentacao" && !i.aguardandoFicha },
  // Só gerente/admin: assinadas aguardando o VÍNCULO com o módulo. Não é fila de
  // aprovação (o credenciamento não espera gerente) — é a lista de quem precisa
  // ser cadastrado no módulo e vinculado; feito isso, vira Ativo sozinha.
  { id: "validar", label: "A vincular", cor: "#3b82f6", gerencial: true, filtro: (i) => ["validacao", "ativacao"].includes(i.estagio) },
  { id: "ativos", label: "Ativos na rede", cor: "#059669", filtro: (i) => i.estagio === "ativo" },
  { id: "fora", label: "Sem interesse", cor: "#d97706", filtro: (i) => ["sem_interesse", "recusado"].includes(i.estagio) },
  // Só aparece para gerente e administrador: é a fila de quem avalia.
  { id: "conferir", label: "A conferir", cor: "#7c3aed", gerencial: true, filtro: (i) => i.parceiro && pendentesDeConferencia(i.parceiro).length > 0 },
];

export default function RedeParceiros({
  partners = [], setPartners, credenciamentos = [], setCredenciamentos,
  search, userName = "", role = "comercial", metas = [], userId, today, usuarios = [],
  setShowNewPartner, setSelPartner, setShowNewInteraction,
  onSendLink, onEditFicha, onPedirDocumentos, onJustificar, onConferir, onAbrirFila,
  onNovaFicha, onRegistroRapido, setTasks, grupoInicial, onGrupoAplicado,
}) {
  // Quem executa é o comercial; quem confere e avalia é a gerência.
  const podeConferir = role === "gerente" || role === "admin";
  // Excluir é destrutivo e definitivo (apaga a ficha, os arquivos e o parceiro):
  // só o administrador, e só para limpar duplicidade — não é passo do fluxo.
  const podeExcluir = role === "admin";
  const grupos = useMemo(() => GRUPOS.filter((g) => !g.gerencial || podeConferir), [podeConferir]);
  const [grupo, setGrupo] = useState("all");
  // Quem chega pela cobrança de meta quer ver QUEM CONTATAR, não a rede
  // inteira. Cair em "Todas" era mandar a pessoa procurar sozinha o que o
  // pop-up acabou de dizer que estava faltando.
  useEffect(() => {
    if (!grupoInicial) return;
    setGrupo(grupoInicial);
    onGrupoAplicado && onGrupoAplicado();
  }, [grupoInicial, onGrupoAplicado]);
  const [q, setQ] = useState("");
  const [ufFilter, setUfFilter] = useState("all");
  const [tipoFilter, setTipoFilter] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(24);
  const [recusando, setRecusando] = useState(null); // item em que o painel de recusa está aberto
  const [modoRecusa, setModoRecusa] = useState("sem_interesse"); // sem_interesse | definitivo | ato do gerente
  const [motivo, setMotivo] = useState("");
  const [retomarEm, setRetomarEm] = useState("");
  const [erroMotivo, setErroMotivo] = useState("");
  const [aExcluir, setAExcluir] = useState(null); // item que o admin quer apagar
  const [lote, setLote] = useState(null); // progresso do vínculo em lote com a manutenção
  // ── Carteira ────────────────────────────────────────────────────
  // Quem responde por cada empresa. A lista é a rede inteira (ver App.js);
  // este filtro é o que separa "o meu dia" de "o que existe para fazer".
  const [donoFilter, setDonoFilter] = useState("all");
  const [transferirLoteDe, setTransferirLoteDe] = useState(""); // destino escolhido no lote
  const [confirmarLote, setConfirmarLote] = useState(false);
  const podeTransferir = role === "admin" || role === "gerente";
  const nomeDoDono = useMemo(() => {
    const m = {};
    for (const u of usuarios) m[u.id] = u.full_name || "sem nome";
    return m;
  }, [usuarios]);

  const todos = useMemo(() => unificarRede(partners, credenciamentos), [partners, credenciamentos]);

  // Fichas da mesma empresa que ficaram separadas (ver duplicatasDaFicha).
  const duplicatas = useMemo(() => {
    const m = {};
    for (const f of credenciamentos) {
      const d = duplicatasDaFicha(f, credenciamentos);
      if (d.length) m[f.id] = d;
    }
    return m;
  }, [credenciamentos]);
  const [aJuntar, setAJuntar] = useState(null); // { a, b } — as duas fichas
  // Junta a duplicata na ficha mais completa e apaga a outra. Nada se perde:
  // histórico (inclusive a prova do contato), documentos, observação e o
  // vínculo com o parceiro vão para a que fica, e a junção entra na trilha.
  const juntarFichas = () => {
    if (!aJuntar) return;
    const [fica, sai] = escolherPrincipal(aJuntar.a, aJuntar.b);
    const junta = mesclarFichas(fica, sai, userName);
    setCredenciamentos((prev) => prev.filter((f) => f.id !== sai.id).map((f) => (f.id === fica.id ? junta : f)));
    setAJuntar(null);
  };
  // O filtro de responsável vale também para os números do funil: um card que
  // diz 309 sobre uma lista de 121 é um card que mente.
  const itens = useMemo(() => {
    if (donoFilter === "all") return todos;
    if (donoFilter === "meus") return todos.filter((i) => i.dono === userId);
    if (donoFilter === "sem_dono") return todos.filter((i) => !i.dono);
    return todos.filter((i) => i.dono === donoFilter);
  }, [todos, donoFilter, userId]);

  // ── Vínculo em lote com os módulos (manutenção e combustível) ──────
  //
  // Um clique reprocessa, em sequência, toda ficha ASSINADA que ainda não
  // fechou o ciclo com o módulo: cadastro (grava a ficha, anexa os documentos e
  // amarra) e depois aceite (registra a assinatura eletrônica).
  //
  // Antes só entrava quem estava em Validação SEM provider_id. Ficavam de fora
  // justamente os que precisavam de nova tentativa: vinculados com o aceite
  // travado ("falta o documento de quem assina" — o documento existia, era a
  // manutenção que não conseguia anexá-lo; corrigido lá em 18/09) e o aceite de
  // termo anterior que não dispensou a assinatura (AUTODRIVE). Esses já estão
  // "Ativos" pelo provider_id, então o filtro de estágio não os via.
  //
  // A manutenção não cria fornecedor (404 = cadastrar lá à mão); o combustível
  // cria. A lista é a rede inteira, não o recorte do filtro de responsável.
  const precisaDoModulo = (f, sistema) => {
    // Posto assinado é do combustível mesmo quando ninguém marcou o sistema na
    // ficha — havia um assim, que o lote deixaria de fora sem ninguém perceber.
    const doSistema = (f?.sistemas || []).includes(sistema) || (sistema === "combustivel" && f?.tipoEstabelecimento === "posto");
    if (!f || !doSistema || !fichaConfirmada(f)) return false;
    const s = f.integracaoSync?.[sistema];
    if (!s?.provider_id) return true;                          // ainda não vinculada
    if (s.ok === false) return true;                           // vinculada, aceite travado
    if (s.acao === "aceite" && s.assinatura_dispensada === false) return true; // aceite sem dispensa
    return s.acao !== "aceite";                                // só o cadastro rodou
  };
  // Quem já está no módulo e só falta fechar o aceite, separado de quem
  // ainda nem foi vinculado — o botão mostrava um número só (60) enquanto o
  // grupo "A vincular" tinha 3, e não dava para saber o que o lote ia fazer.
  const semVinculo = (f, sistema) => !f.integracaoSync?.[sistema]?.provider_id;
  // Duas filas, e cada uma mora no seu grupo:
  //   vincular  — ainda não existe no módulo: é a fila de "A vincular";
  //   aceite    — já está lá (aparece como Ativo), só o aceite não fechou.
  // Um botão só, no topo de todos os grupos, misturava as duas: dizia 60
  // enquanto o grupo "A vincular" mostrava 3, e ninguém sabia o que ia rodar.
  const pendentesModulo = useMemo(() => {
    const monta = (sis) => {
      const alvos = todos.filter((i) => i.ficha && precisaDoModulo(i.ficha, sis));
      return { vincular: alvos.filter((i) => semVinculo(i.ficha, sis)), aceite: alvos.filter((i) => !semVinculo(i.ficha, sis)) };
    };
    return { manutencao: monta("manutencao"), combustivel: monta("combustivel") };
  }, [todos]);

  const vincularLote = async (sistema, fila = "vincular") => {
    const alvos = pendentesModulo[sistema]?.[fila] || [];
    if (!alvos.length || lote) return;
    setLote({ sistema, feito: 0, total: alvos.length, aceitos: 0, travados: 0, naoExiste: 0, erros: [], rodando: true });
    for (const item of alvos) {
      const fichaId = item.ficha.id;
      // Cadastro primeiro; se falhar (fornecedor não existe), nem tenta o
      // aceite — o resultado do cadastro já explica o porquê.
      const rc = await sincronizarCadastro(fichaId, undefined, sistema).catch(() => null);
      let sync = rc?.sync || null;
      if (rc?.ok) {
        const ra = await sincronizarAceite(fichaId, sistema).catch(() => null);
        sync = ra?.sync || sync;
      }
      if (sync) setCredenciamentos((prev) => prev.map((f) => (f.id !== fichaId ? f : {
        ...f, integracaoSync: { ...(f.integracaoSync || {}), [sistema]: sync },
      })));
      const aceito = !!sync?.provider_id && sync?.acao === "aceite" && sync?.ok !== false;
      const naoExiste = !sync?.provider_id;
      setLote((v) => v && ({
        ...v, feito: v.feito + 1,
        aceitos: v.aceitos + (aceito ? 1 : 0),
        naoExiste: v.naoExiste + (naoExiste ? 1 : 0),
        travados: v.travados + (!aceito && !naoExiste ? 1 : 0),
        erros: !aceito && sync?.erro ? [...v.erros, `${item.nome}: ${sync.erro}`] : v.erros,
      }));
    }
    setLote((v) => v && ({ ...v, rodando: false }));
  };

  const contagem = useMemo(() => {
    const c = {};
    for (const g of grupos) c[g.id] = itens.filter(g.filtro).length;
    return c;
  }, [itens, grupos]);

  const filtrados = useMemo(() => {
    setPage(1);
    const texto = (q || search || "").toLowerCase().trim();
    const g = grupos.find((x) => x.id === grupo) || grupos[0];
    return itens
      .filter(g.filtro)
      .filter((i) => (ufFilter === "all" || i.uf === ufFilter))
      .filter((i) => (tipoFilter === "all" || i.tipo === tipoFilter))
      .filter((i) => !texto || `${i.nome} ${i.cnpj} ${i.contato} ${i.cidade}`.toLowerCase().includes(texto))
      .sort((a, b) => `${b.criadoEm}${b.chave}`.localeCompare(`${a.criadoEm}${a.chave}`));
  }, [itens, grupos, grupo, ufFilter, tipoFilter, q, search]);

  const pagina = useMemo(() => filtrados.slice((page - 1) * pageSize, page * pageSize), [filtrados, page, pageSize]);

  // ── Trilha: toda mudança de estágio registra quem, quando e por quê ──
  // O `trilha` carrega o dado estruturado do evento (motivo, tipo). Ele vai
  // DENTRO do histórico de propósito: campo solto que o banco não conhece é
  // descartado na gravação, e o motivo sumiria na primeira recarga.
  const moverParceiro = (item, estagio, nota, extra = {}, trilha = {}) => {
    if (!item.parceiro) return;
    setPartners((prev) => prev.map((p) => (p.id !== item.parceiro.id ? p : {
      ...p, stage: estagio, ...extra,
      stageHistory: [...(p.stageHistory || []), { from: p.stage, to: estagio, date: getToday(), note: nota, autor: userName, ...trilha }],
    })));
  };

  const moverFicha = (item, patch, detalhe, trilha = {}) => {
    if (!item.ficha) return;
    setCredenciamentos((prev) => prev.map((f) => (f.id !== item.ficha.id ? f : {
      ...f, ...patch,
      historico: [...(Array.isArray(f.historico) ? f.historico : []), {
        ts: new Date().toISOString(), acao: patch.status === "sem_interesse" ? "sem_interesse" : "edicao",
        detalhe, autor: userName, ...trilha,
      }],
    })));
  };

  // "Aceitou" não move mais o estágio sozinho. Mover direto era a porta dos
  // fundos da regra da prova: o card ia para documentação sem export e sem
  // registro telefônico. Agora abre o registro de contato já marcado em
  // "Aceitou credenciar" — e o avanço acontece lá, com a prova.
  const aceitou = (item) => {
    if (item.parceiro) setShowNewInteraction({ type: "partner", id: item.parceiro.id, resultado: "aceitou" });
  };

  const abrirRecusa = (item, modo = "sem_interesse") => {
    setRecusando(item);
    setModoRecusa(modo);
    setMotivo(item.motivoSemInteresse || "");
    setRetomarEm(addMonths(getToday(), 3));
    setErroMotivo("");
  };

  // O gerente aprova ou reprova — e reprovar é definitivo, não vira retomada.
  const executarAtoGerente = (item, ato, motivoTexto = "") => {
    if (!item.parceiro) return;
    setPartners((prev) => prev.map((p) => (
      p.id === item.parceiro.id ? aplicarAtoGerente(p, ato, { motivo: motivoTexto, autor: userName }) : p
    )));
    if (item.ficha && ato.para === "recusado") {
      moverFicha(item, { status: "sem_interesse" }, `${ato.nota}: ${motivoTexto}`, { motivo: motivoTexto, definitivo: true });
    }
  };

  const confirmarRecusa = () => {
    // Motivo obrigatório em qualquer caminho: recusa sem porquê não ensina nada
    // a ninguém, e é o que faz a retomada (ou a não-retomada) valer alguma coisa.
    if (!motivo.trim()) { setErroMotivo("Diga o motivo — é o que vai orientar a retomada."); return; }
    const item = recusando;

    // Ato do gerente (reprovação de documentação/ativação): sai do funil de vez.
    if (modoRecusa && typeof modoRecusa === "object") {
      executarAtoGerente(item, modoRecusa, motivo.trim());
      setRecusando(null); setMotivo(""); setErroMotivo("");
      return;
    }

    // Recusa definitiva: desistiu de vez, não volta para a fila de retomada.
    // Antes tudo virava "sem interesse" com retomada em 3 meses — inclusive o
    // "não" definitivo, que voltava a incomodar a equipe sem motivo nenhum.
    if (modoRecusa === "definitivo") {
      const nota = `Recusado definitivamente: ${motivo.trim()}`;
      // Definitivo não volta: qualquer retomada pendente é encerrada.
      if (setTasks && item.parceiro) setTasks((prev) => concluirDe(prev, item.parceiro.id, TITULO_RETOMADA));
      moverParceiro(item, "recusado", nota, { nextAction: "", nextActionDate: "" }, { tipo: "recusado", motivo: motivo.trim(), definitivo: true });
      if (item.ficha) moverFicha(item, { status: "sem_interesse", nextAction: null, nextActionDate: null }, nota, { motivo: motivo.trim(), definitivo: true });
      setRecusando(null); setMotivo(""); setErroMotivo("");
      return;
    }

    const nota = `Sem interesse: ${motivo.trim()}`;
    moverParceiro(item, "sem_interesse", nota, {
      nextAction: "Retomar contato (sem interesse anterior)",
      nextActionDate: retomarEm || addMonths(getToday(), 3),
    }, { tipo: "sem_interesse", motivo: motivo.trim() });
    if (item.ficha) {
      moverFicha(item, {
        status: "sem_interesse",
        nextAction: "Retomar contato (sem interesse anterior)",
        nextActionDate: retomarEm || addMonths(getToday(), 3),
      }, nota, { motivo: motivo.trim() });
    }
    // A retomada vira TAREFA. Antes a data ficava só no card e ninguém era
    // avisado no dia — a promessa de voltar em 3 meses morria ali.
    if (setTasks && item.parceiro) {
      const quando = retomarEm || addMonths(getToday(), 3);
      setTasks((prev) => agendar(prev, {
        refId: item.parceiro.id, titulo: `${TITULO_RETOMADA} — ${item.nome}`,
        quando, userId, notas: `Sem interesse em ${getToday()}: ${motivo.trim()}`,
      }));
    }
    setRecusando(null); setMotivo(""); setErroMotivo("");
  };

  // ── Empresas que nasceram na ficha ──────────────────────────────
  //
  // Quem entrou pelo link (ou por uma ficha criada na visita) aparece em
  // "documentação" porque é o que a ficha diz — mas muitas nunca receberam
  // contato registrado, e o lugar delas é abordagem, ou nem isso.
  //
  // NÃO mexemos nelas sozinhos. Reverter estágio em massa reescreveria o
  // passado de 64 fichas com base num palpite. O que se ganha aqui é o botão:
  // a pessoa clica e diz onde a empresa está de verdade, e isso fica na trilha
  // com o nome dela.
  const trazerParaFunil = (item, estagio = "identificado") => {
    if (item.parceiro) {
      moverParceiro(item, estagio, `Estágio corrigido para ${estagio} — cadastro antigo, sem contato registrado`, {}, { tipo: "correcao_estagio" });
      return;
    }
    // Ficha sem parceiro: antes de criar, procura a empresa na rede (mesmo CNPJ
    // liga direto; mesmo telefone pergunta). Ver parceiroExistenteDaFicha.
    const f = item.ficha;
    if (!f) return null;
    const { certo, candidatos } = parceiroExistenteDaFicha(f, partners);
    const existente = certo || (candidatos.length === 1 && window.confirm(
      `Já existe "${candidatos[0].name}" na rede com o mesmo telefone. Ligar esta ficha a esse cadastro em vez de criar outro?`,
    ) ? candidatos[0] : null);
    if (existente) {
      setCredenciamentos((prev) => prev.map((x) => (x.id !== f.id ? x : {
        ...x, partnerId: existente.id,
        historico: [...(Array.isArray(x.historico) ? x.historico : []), {
          ts: new Date().toISOString(), acao: "edicao", autor: userName,
          detalhe: `Ficha ligada ao parceiro que já existia na rede ("${existente.name}").`,
        }],
      })));
      return existente.id;
    }
    const novo = {
      // O parceiro nasce com o dono da FICHA, não com quem clicou: quando o
      // administrador arrumava o estágio de uma ficha da Mariely, a empresa
      // trocava de dono no caminho e sumia da carteira dela.
      // Ficha sem responsável (aberta para o time) gera parceiro também sem.
      id: generateId(), userId: f.userId ?? null,
      name: f.nomeFantasia || f.razaoSocial || "Sem nome",
      razaoSocial: f.razaoSocial || "", nomeFantasia: f.nomeFantasia || "",
      cnpj: f.cnpj || "", type: f.tipoEstabelecimento || "oficina",
      contact: f.responsavelNome || "", phone: f.celular || f.telefone || "",
      email: f.email || "", region: f.estado || "", cidade: f.cidade || "",
      address: f.enderecoCompleto || "", services: "", notes: "",
      stage: estagio, createdAt: getToday(), interactions: [], docs: [],
      stageHistory: [{
        from: null, to: estagio, date: getToday(), autor: userName,
        tipo: "correcao_estagio", note: "Trazida para o funil a partir da ficha existente",
      }],
    };
    setPartners((prev) => [...prev, novo]);
    setCredenciamentos((prev) => prev.map((x) => (x.id !== f.id ? x : {
      ...x, partnerId: novo.id,
      historico: [...(Array.isArray(x.historico) ? x.historico : []), {
        ts: new Date().toISOString(), acao: "edicao", autor: userName,
        detalhe: "Ficha ligada ao funil da rede (parceiro criado a partir dela).",
      }],
    })));
    return novo.id;
  };

  // Registrar contato numa empresa que só tem ficha (registro rápido, link,
  // posto vindo do combustível). Antes o botão simplesmente não existia nesses
  // cards: para lançar a conversa era preciso descobrir que "A contatar" criava
  // o parceiro primeiro. Agora é um clique só — cria e já abre o registro.
  const contatar = (item) => {
    if (item.parceiro) { setShowNewInteraction({ type: "partner", id: item.parceiro.id }); return; }
    // O parceiro nasce NO estágio em que a empresa já está. Nascia em
    // "identificado", e como o estágio do parceiro manda sobre o da ficha,
    // registrar a conversa de quem já assinou o devolvia para "a contatar".
    const novoId = trazerParaFunil(item, item.estagio || "identificado");
    if (novoId) setShowNewInteraction({ type: "partner", id: novoId });
  };

  // ── Carteira: transferir e assumir ──────────────────────────────
  //
  // A rede nasceu com 300+ empresas na mão de quem importou a lista, e não
  // havia como passar nenhuma adiante pela tela — só por SQL. Aqui a troca de
  // dono é um ato registrado: o `userId` muda nos dois lados (parceiro e ficha)
  // e a trilha guarda de quem para quem, com o nome de quem mandou. A gravação
  // do `user_id` só acontece quando ele MUDA (ver syncChanges) — foi o que
  // fazia a transferência sumir na recarga.
  const trocarDono = (item, paraId, motivo = "") => {
    if (!paraId) return;
    const nome = nomeDoDono[paraId] || "outro usuário";
    const nota = motivo || `Carteira transferida para ${nome}`;
    if (item.parceiro) {
      setPartners((prev) => prev.map((p) => (p.id !== item.parceiro.id ? p : {
        ...p, userId: paraId,
        stageHistory: [...(p.stageHistory || []), {
          from: p.stage, to: p.stage, date: getToday(), autor: userName,
          tipo: "transferencia", de: item.dono || null, para: paraId, note: nota,
        }],
      })));
    }
    if (item.ficha) {
      setCredenciamentos((prev) => prev.map((f) => (f.id !== item.ficha.id ? f : {
        ...f, userId: paraId,
        historico: [...(Array.isArray(f.historico) ? f.historico : []), {
          ts: new Date().toISOString(), acao: "edicao", autor: userName,
          tipo: "transferencia", de: item.dono || null, para: paraId, detalhe: nota,
        }],
      })));
    }
  };

  const transferirFiltrados = () => {
    const destino = transferirLoteDe;
    setConfirmarLote(false);
    if (!destino) return;
    for (const item of filtrados) {
      if (item.dono === destino) continue;
      trocarDono(item, destino, `Transferência em lote para ${nomeDoDono[destino] || "outro usuário"}`);
    }
    setTransferirLoteDe("");
  };

  // ── Posto que chegou do combustível sem telefone ────────────────
  //
  // O candidato vem da rede de abastecimento com CNPJ e nome, e quase nunca com
  // telefone ou responsável (a integração manda o que tem). O card ficava numa
  // fila que ninguém conseguia trabalhar: "a contatar" sem por onde contatar.
  // A consulta pública por CNPJ é grátis e já existe no CRM — aqui ela preenche
  // contato, endereço e e-mail sem pedir nada a ninguém.
  const [buscandoCnpj, setBuscandoCnpj] = useState(null);
  const completarPeloCnpj = async (item) => {
    const cnpj = (item.cnpj || "").replace(/\D/g, "");
    if (cnpj.length !== 14 || !item.parceiro || buscandoCnpj) return;
    setBuscandoCnpj(item.chave);
    const r = await fetchCNPJ(cnpj).catch(() => null);
    setBuscandoCnpj(null);
    if (!r) return;
    setPartners((prev) => prev.map((p) => (p.id !== item.parceiro.id ? p : {
      ...p,
      name: p.name || r.nomeFantasia || r.razaoSocial || "",
      razaoSocial: p.razaoSocial || r.razaoSocial || "",
      phone: p.phone || r.celular || r.telefone || "",
      email: p.email || r.email || "",
      contact: p.contact || r.representanteNome || "",
      region: p.region || r.uf || "",
      cidade: p.cidade || r.cidade || "",
      address: p.address || [r.rua, r.numero, r.bairro].filter(Boolean).join(", "),
      stageHistory: [...(p.stageHistory || []), {
        from: p.stage, to: p.stage, date: getToday(), autor: userName,
        tipo: "dados_receita", note: "Contato e endereço preenchidos pela consulta pública do CNPJ",
      }],
    })));
  };

  // Empresa da pilha comum (sem dono, ou de outra pessoa) passa a ser de quem
  // pegou o telefone. Sem isto, o contato ia para o card e a empresa continuava
  // "do administrador" — e no dia seguinte outra pessoa ligava de novo.
  const assumir = (item) => trocarDono(item, userId, `${userName || "Usuário"} assumiu este contato`);

  const retomar = (item) => {
    moverParceiro(item, "abordagem", "Contato retomado", { nextAction: "", nextActionDate: "" }, { tipo: "retomada" });
    if (setTasks && item.parceiro) setTasks((prev) => concluirDe(prev, item.parceiro.id, TITULO_RETOMADA));
    if (item.ficha) moverFicha(item, { status: "pendente", nextActionDate: null, nextAction: null }, "Contato retomado");
  };

  // ── Excluir (admin) ─────────────────────────────────────────────
  //
  // Existe para a limpeza de duplicidade: a mesma empresa entrou duas vezes
  // (registro rápido + link, dois registros rápidos) e uma das cópias precisa
  // sair. Apaga os dois lados do card — a ficha (com os arquivos no Storage) e
  // o parceiro — porque deixar metade órfã recriaria a bagunça que a exclusão
  // veio resolver. Sem volta: por isso passa por confirmação e só o admin vê.
  const excluirItem = async () => {
    const item = aExcluir;
    setAExcluir(null);
    if (!item) return;
    if (item.ficha) {
      const paths = [item.ficha.fichaPath, item.ficha.assinaturaPath, ...(item.ficha.documentos || []).map((d) => d.path)].filter(Boolean);
      try { await removeFromStorage(paths); } catch (e) { console.warn("remoção de arquivos da ficha falhou:", e); }
      setCredenciamentos((prev) => prev.filter((x) => x.id !== item.ficha.id));
    }
    if (item.parceiro) setPartners((prev) => prev.filter((p) => p.id !== item.parceiro.id));
  };

  const exportar = () => exportCSV(filtrados.map((i) => ({
    Empresa: i.nome, CNPJ: i.cnpj, Estágio: PARTNER_STAGES.find((s) => s.id === i.estagio)?.label || i.estagio,
    Contato: i.contato, Telefone: i.telefone, Cidade: i.cidade, UF: i.uf,
    "Último contato": i.ultimoContato || "", "Tem ficha": i.ficha ? "Sim" : "Não",
    "Situação da ficha": i.ficha ? credStatus(i.ficha).label : "",
    "Motivo (sem interesse)": i.motivoSemInteresse || "", "Retomar em": i.retomarEm || "",
    "Dias parado": i.parceiro ? diasParado(i.parceiro) : "",
    "Contato comprovado": i.parceiro ? (ultimoComprovante(i.parceiro) ? "Sim" : "Não") : "",
    "Justificativa da espera": (() => {
      const j = i.parceiro && justificativaVigente(i.parceiro);
      return j ? `${rotuloMotivo(j.motivo)}${j.detalhe ? ` — ${j.detalhe}` : ""}` : "";
    })(),
    "Nova tentativa em": (justificativaVigente(i.parceiro || {}) || {}).proximaTentativa || "",
    "Pendente de conferência": i.parceiro ? pendentesDeConferencia(i.parceiro).length : "",
    "Avaliados como fracos": i.parceiro ? resumoQualidade(i.parceiro).fraco : "",
  })), "rede_parceiros_instasolutions");

  const sel = { padding: "7px 10px", borderRadius: 7, border: "1px solid #d1d5db", fontSize: 12, background: "#fff", cursor: "pointer", fontFamily: "inherit", width: "100%" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <GuideBanner
        title="Rede de Parceiros — onde o contato começa"
        text={`Prospecção, contato e aceite acontecem aqui. O estágio anda pelo que você FAZ: registrou contato vira abordagem, ele aceitou vai para documentação — e daí em diante o trabalho continua na ficha, pelo botão do próprio card. Todo contato por WhatsApp entra com o print, e quem fica ${PRAZO_ABORDAGEM_DIAS} dias em abordagem sem contato precisa justificar a espera.`}
      />

      {/* Meta do dia — em cima de tudo, porque é o que organiza o dia de quem
          está com a tela aberta. */}
      <PainelMeta partners={partners} metas={metas} userId={userId} role={role} hoje={today} />

      {/* ── Funil em números: cada número é um filtro ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
        {grupos.map((g) => (
          <div
            key={g.id}
            onClick={() => setGrupo(g.id)}
            style={{
              background: "#fff", borderRadius: 10, padding: "12px 14px", cursor: "pointer",
              border: `1px solid ${grupo === g.id ? g.cor : "#e5e7eb"}`, borderLeft: `4px solid ${g.cor}`,
              boxShadow: grupo === g.id ? `0 0 0 1px ${g.cor}` : "none",
            }}
          >
            <div style={{ fontSize: 22, fontWeight: 800, color: g.cor }}>{contagem[g.id]}</div>
            <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>{g.label}</div>
          </div>
        ))}
      </div>

      {/* ── Barra de ações ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ position: "relative" }}>
            <Search size={13} color="#94a3b8" style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)" }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Empresa, CNPJ, contato..." style={{ ...sel, cursor: "text", paddingLeft: 28, width: 240 }} />
          </div>
          <button onClick={() => setShowFilters((v) => !v)} style={{ display: "flex", alignItems: "center", gap: 5, padding: "7px 14px", borderRadius: 7, border: `1px solid ${(ufFilter !== "all" || tipoFilter !== "all") ? "#7c3aed" : "#d1d5db"}`, background: (ufFilter !== "all" || tipoFilter !== "all") ? "#f5f3ff" : "#fff", color: (ufFilter !== "all" || tipoFilter !== "all") ? "#6d28d9" : "#475569", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            <Filter size={13} /> Filtros
          </button>
          {/* Responsável: a rede é de todos, o dia é de cada um. "A contatar"
              não é de ninguém ainda — e por isso aparece para o time inteiro. */}
          <select
            value={donoFilter} onChange={(e) => setDonoFilter(e.target.value)}
            title="De quem são as empresas mostradas"
            style={{ ...sel, width: "auto", borderColor: donoFilter !== "all" ? "#7c3aed" : "#d1d5db", color: donoFilter !== "all" ? "#6d28d9" : "#475569", fontWeight: 600 }}
          >
            <option value="all">Toda a rede ({todos.length})</option>
            <option value="meus">Só os meus</option>
            <option value="sem_dono">Sem responsável</option>
            {usuarios.map((u) => <option key={u.id} value={u.id}>{u.full_name || "sem nome"}</option>)}
          </select>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {podeConferir && contagem.conferir > 0 && (
            <button onClick={onAbrirFila} style={{ ...btnSec, borderColor: "#ddd6fe", background: "#f5f3ff", color: "#6d28d9" }}>
              <ClipboardCheck size={13} /> Conferir fila ({contagem.conferir})
            </button>
          )}
          {/* Cada lote no grupo a que pertence: "Vincular" dentro de A vincular,
              "Fechar aceite" dentro de Ativos — no topo de todos os grupos, o
              botão aparecia até para quem estava olhando "A contatar". */}
          {podeConferir && [["manutencao", "manutenção"], ["combustivel", "combustível"]].map(([sis, rotulo]) => {
            const fila = grupo === "validar" ? "vincular" : grupo === "ativos" ? "aceite" : null;
            const qtd = fila ? pendentesModulo[sis][fila].length : 0;
            return qtd > 0 && (
              <button key={sis} onClick={() => vincularLote(sis, fila)} disabled={!!lote?.rodando} title={fila === "vincular"
                ? `Cadastra e vincula no ${rotulo} as fichas CONFIRMADAS (enviadas e concluídas, com assinatura) que ainda não existem lá. Ficha pendente não entra.`
                : `Já estão no ${rotulo}: reenvia o aceite das que ficaram com pendência (documento de quem assina, termo).`} style={{ ...btnSec, borderColor: "#93c5fd", background: "#eff6ff", color: "#1d4ed8", opacity: lote?.rodando ? 0.7 : 1, cursor: lote?.rodando ? "wait" : "pointer" }}>
                <RotateCcw size={13} /> {lote?.rodando && lote.sistema === sis
                  ? `Enviando ${lote.feito}/${lote.total}...`
                  : `${fila === "vincular" ? "Vincular" : "Fechar aceite"} ${rotulo} (${qtd})`}
              </button>
            );
          })}
          {/* Transferência em lote: é assim que as centenas que ficaram com
              quem importou a lista chegam a quem vai contatar. Passa pelo mesmo
              registro na trilha, uma empresa por vez. */}
          {podeTransferir && filtrados.length > 0 && (
            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
              <select value={transferirLoteDe} onChange={(e) => setTransferirLoteDe(e.target.value)} style={{ ...sel, width: "auto" }}>
                <option value="">Transferir para...</option>
                {usuarios.map((u) => <option key={u.id} value={u.id}>{u.full_name || "sem nome"}</option>)}
              </select>
              <button
                onClick={() => setConfirmarLote(true)} disabled={!transferirLoteDe}
                style={{ ...btnSec, borderColor: transferirLoteDe ? "#7c3aed" : "#e2e8f0", color: transferirLoteDe ? "#6d28d9" : "#cbd5e1", cursor: transferirLoteDe ? "pointer" : "not-allowed" }}
                title="Transferir todas as empresas da lista filtrada"
              >
                <ArrowRight size={13} /> {filtrados.length} {filtrados.length === 1 ? "empresa" : "empresas"}
              </button>
            </div>
          )}
          <button onClick={exportar} style={btnSec}>CSV</button>
          {/* As três portas de entrada ficam aqui, junto de quem prospecta:
              procurar a tela certa para cadastrar era atrito puro. */}
          {onRegistroRapido && (
            <button onClick={onRegistroRapido} style={btnSec} title="Anotou na rua? Registre o essencial agora e complete depois">
              <Zap size={13} /> Registro rápido
            </button>
          )}
          <button onClick={() => onSendLink && onSendLink(null)} style={{ ...btnSec, borderColor: "#7c3aed", background: "#f5f3ff", color: "#6d28d9" }}>
            <Send size={13} /> Enviar formulário
          </button>
          {onNovaFicha && (
            <button onClick={onNovaFicha} style={btnSec} title="Preencher a ficha completa agora (visita)">
              <ClipboardCheck size={13} /> Nova ficha
            </button>
          )}
          <button onClick={() => setShowNewPartner(true)} style={{ display: "flex", alignItems: "center", gap: 5, padding: "8px 16px", borderRadius: 7, border: "none", background: "#7c3aed", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            <Plus size={14} /> Nova empresa
          </button>
        </div>
      </div>

      {/* Resultado do vínculo em lote */}
      {lote && !lote.rodando && (
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, flexWrap: "wrap", padding: "10px 14px", borderRadius: 10, background: (lote.travados + lote.naoExiste) > 0 ? "#fffbeb" : "#f0fdf4", border: `1px solid ${(lote.travados + lote.naoExiste) > 0 ? "#fde68a" : "#bbf7d0"}` }}>
          <div style={{ fontSize: 12, color: (lote.travados + lote.naoExiste) > 0 ? "#92400e" : "#166534", lineHeight: 1.5, flex: 1, minWidth: 240 }}>
            <strong>{lote.sistema === "combustivel" ? "Combustível" : "Manutenção"}:</strong>{" "}
            <strong>{lote.aceitos}</strong> com cadastro e aceite fechados
            {lote.travados > 0 && <> · <strong>{lote.travados}</strong> vinculados com pendência</>}
            {lote.naoExiste > 0 && <> · <strong>{lote.naoExiste}</strong> sem fornecedor no módulo{lote.sistema === "manutencao" ? " — cadastre lá à mão e rode de novo" : ""}</>}
            {lote.erros.length > 0 && (
              <details style={{ marginTop: 4 }}>
                <summary style={{ cursor: "pointer" }}>Ver o motivo de cada um</summary>
                <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>{lote.erros.map((e, i) => <li key={i}>{e}</li>)}</ul>
              </details>
            )}
          </div>
          <button onClick={() => setLote(null)} style={{ ...btnSec, padding: "5px 12px" }}>Fechar</button>
        </div>
      )}

      {showFilters && (
        <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb", padding: 14, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
          <div>
            <Lbl>Estado (UF)</Lbl>
            <select style={sel} value={ufFilter} onChange={(e) => setUfFilter(e.target.value)}>
              <option value="all">Todos</option>
              {Array.from(new Set(itens.map((i) => i.uf).filter(Boolean))).sort().map((uf) => <option key={uf} value={uf}>{uf}</option>)}
            </select>
          </div>
          <div>
            <Lbl>Ramo</Lbl>
            <select style={sel} value={tipoFilter} onChange={(e) => setTipoFilter(e.target.value)}>
              <option value="all">Todos</option>
              {PARTNER_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>
          <div style={{ display: "flex", alignItems: "flex-end" }}>
            <button onClick={() => { setUfFilter("all"); setTipoFilter("all"); setQ(""); }} style={{ ...btnSec, width: "100%", justifyContent: "center" }}><X size={12} /> Limpar</button>
          </div>
        </div>
      )}

      {/* ── Cards ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 10 }}>
        {pagina.length === 0 && (
          <div style={{ gridColumn: "1/-1", padding: 40, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
            Nenhuma empresa aqui. Comece por <strong>Nova empresa</strong> — é só o CNPJ, o resto vem sozinho.
          </div>
        )}
        {pagina.map((item) => (
          <Card
            key={item.chave}
            item={item}
            aberto={recusando?.chave === item.chave}
            motivo={motivo} setMotivo={setMotivo}
            retomarEm={retomarEm} setRetomarEm={setRetomarEm}
            erroMotivo={erroMotivo}
            modoRecusa={modoRecusa} setModoRecusa={setModoRecusa}
            onAbrirRecusa={(modo) => abrirRecusa(item, modo)}
            onCancelarRecusa={() => { setRecusando(null); setErroMotivo(""); }}
            onConfirmarRecusa={confirmarRecusa}
            onContato={() => contatar(item)}
            duplicatas={item.ficha ? duplicatas[item.ficha.id] || [] : []}
            onJuntar={(outra) => setAJuntar({ a: item.ficha, b: outra })}
            usuarios={usuarios}
            nomeDoDono={nomeDoDono}
            userId={userId}
            podeTransferir={podeTransferir}
            onTransferir={(paraId) => trocarDono(item, paraId)}
            onAssumir={() => assumir(item)}
            buscandoCnpj={buscandoCnpj === item.chave}
            onCompletarPeloCnpj={() => completarPeloCnpj(item)}
            onAceitou={() => aceitou(item)}
            onRetomar={() => retomar(item)}
            onDetalhe={() => item.parceiro && setSelPartner(item.parceiro)}
            onFicha={() => item.ficha && onEditFicha && onEditFicha(item.ficha)}
            onFormulario={() => onSendLink && onSendLink(item.parceiro)}
            onDocumentos={() => item.ficha && onPedirDocumentos && onPedirDocumentos(item.ficha)}
            onJustificar={() => item.parceiro && onJustificar && onJustificar(item.parceiro.id)}
            podeConferir={podeConferir}
            onConferir={() => item.parceiro && onConferir && onConferir(item.parceiro.id)}
            onAtoGerente={(ato) => executarAtoGerente(item, ato)}
            onTrazerParaFunil={(estagio) => trazerParaFunil(item, estagio)}
            podeExcluir={podeExcluir}
            onExcluir={() => setAExcluir(item)}
          />
        ))}
      </div>

      <ConfirmDialog
        open={!!aJuntar}
        onClose={() => setAJuntar(null)}
        onConfirm={juntarFichas}
        confirmLabel="Juntar"
        title="Juntar fichas da mesma empresa"
        message={aJuntar ? (() => {
          const [fica, sai] = escolherPrincipal(aJuntar.a, aJuntar.b);
          return `Fica a ficha "${fica.nomeFantasia || fica.razaoSocial || "sem nome"}" (${fica.status}), que é a mais completa. A outra — "${sai.nomeFantasia || sai.razaoSocial || "sem nome"}" (${sai.status}) — é apagada, mas o histórico, a prova do contato, os documentos e o vínculo com o parceiro vão para a que fica.`;
        })() : ""}
      />

      <ConfirmDialog
        open={confirmarLote}
        onClose={() => setConfirmarLote(false)}
        onConfirm={transferirFiltrados}
        confirmLabel="Transferir"
        title="Transferir carteira"
        message={`Passar ${filtrados.length} ${filtrados.length === 1 ? "empresa" : "empresas"} desta lista para ${nomeDoDono[transferirLoteDe] || "outro usuário"}? Cada uma fica registrada na trilha, com de quem para quem — dá para transferir de volta depois.`}
      />

      <ConfirmDialog
        open={!!aExcluir}
        onClose={() => setAExcluir(null)}
        onConfirm={excluirItem}
        danger
        confirmLabel="Excluir"
        title="Excluir empresa da rede"
        message={`Excluir "${aExcluir?.nome || ""}" da rede? ${
          aExcluir?.ficha ? "A ficha e os arquivos (PDF e documentos) serão removidos. " : ""
        }Isso é definitivo e serve só para limpar duplicidade.`}
      />

      <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb" }}>
        <Pagination page={page} totalItems={filtrados.length} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={setPageSize} />
      </div>
    </div>
  );
}

function Card({
  item, aberto, motivo, setMotivo, retomarEm, setRetomarEm, erroMotivo,
  modoRecusa, setModoRecusa,
  onAbrirRecusa, onCancelarRecusa, onConfirmarRecusa,
  onContato, onAceitou, onRetomar, onDetalhe, onFicha, onFormulario, onDocumentos, onJustificar,
  podeConferir, onConferir, onAtoGerente, onTrazerParaFunil, podeExcluir, onExcluir,
  usuarios = [], nomeDoDono = {}, userId, podeTransferir, onTransferir, onAssumir,
  buscandoCnpj, onCompletarPeloCnpj, duplicatas = [], onJuntar,
}) {
  const estagio = PARTNER_STAGES.find((s) => s.id === item.estagio);
  const acoes = acoesDoEstagio(item.estagio);
  const wa = waLink(item.telefone);
  const st = item.ficha ? credStatus(item.ficha) : null;
  const semParceiro = !item.parceiro;
  const jaAssinou = ["validacao", "ativacao", "ativo"].includes(item.estagio) || fichaConfirmada(item.ficha);
  // Estágio à frente sem nenhuma conversa registrada = veio da ficha. É a
  // situação que o botão de correção existe para resolver.
  const semContato = !!item.parceiro
    && !(item.parceiro.interactions || []).length
    && ["documentacao", "validacao", "ativacao", "abordagem"].includes(item.estagio);
  const parado = item.parceiro ? precisaJustificar(item.parceiro) : false;
  const espera = item.parceiro ? justificativaVigente(item.parceiro) : null;
  const prova = item.parceiro ? ultimoComprovante(item.parceiro) : null;
  const aConferir = item.parceiro ? pendentesDeConferencia(item.parceiro).length : 0;
  const correcao = item.parceiro ? correcaoAberta(item.parceiro) : null;
  const qualidade = item.parceiro ? resumoQualidade(item.parceiro) : { total: 0 };
  const atos = item.parceiro ? atosDoGerente(item.estagio) : [];
  // Da documentação em diante, quem conduz é a esteira da ficha.
  const emDocumentacao = ["documentacao", "validacao", "ativacao"].includes(item.estagio);
  const ev = evidencia(item.ficha);
  const meu = item.dono === userId;

  return (
    <div style={{ background: "#fff", borderRadius: 10, border: `1px solid ${parado ? "#fecaca" : "#e5e7eb"}`, borderLeft: `4px solid ${parado ? "#dc2626" : (estagio?.color || "#cbd5e1")}`, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
      {/* Cabeçalho */}
      <div onClick={onDetalhe} style={{ cursor: item.parceiro ? "pointer" : "default" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.nome}</div>
            <div style={{ fontSize: 10.5, color: "#64748b" }}>
              {PARTNER_TYPES.find((t) => t.id === item.tipo)?.label || "Empresa"}{item.cnpj ? ` · ${item.cnpj}` : ""}
            </div>
          </div>
          {item.aguardandoFicha
            ? <Badge color="#8b5cf6">Aguardando ficha</Badge>
            : <Badge color={estagio?.color}>{estagio?.label}</Badge>}
        </div>
        <div style={{ fontSize: 11, color: "#475569", marginTop: 6, display: "flex", gap: 10, flexWrap: "wrap" }}>
          {item.contato && <span><Phone size={10} style={{ verticalAlign: "-1px", marginRight: 3 }} />{item.contato}</span>}
          {item.telefone && <span><MessageCircle size={10} style={{ verticalAlign: "-1px", marginRight: 3 }} />{item.telefone}</span>}
          <span><MapPin size={10} style={{ verticalAlign: "-1px", marginRight: 3 }} />{[item.cidade, stateLabel(item.uf)].filter(Boolean).join("/")}</span>
        </div>
      </div>

      {/* Quem responde por esta empresa + o selo da conversa validada.
          O selo só aparece com o export do WhatsApp anexado: é a única prova
          que traz hora real e assinatura da plataforma. */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", fontSize: 10 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 10, background: meu ? "#eef2ff" : "#f8fafc", border: `1px solid ${meu ? "#c7d2fe" : "#e2e8f0"}`, color: meu ? "#4338ca" : "#64748b", fontWeight: 600 }}>
          {item.dono ? (meu ? "Você" : (nomeDoDono[item.dono] || "outro usuário")) : "Sem responsável"}
        </span>
        {item.conversa?.ok && (
          <span
            title={`Export do WhatsApp anexado${item.conversa.mensagens ? ` — ${item.conversa.mensagens} mensagens` : ""}${item.conversa.quando ? ` em ${fmtShort(item.conversa.quando)}` : ""}`}
            style={{ display: "flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 10, background: "#f0fdfa", border: "1px solid #5eead4", color: "#0f766e", fontWeight: 700 }}
          >
            <ShieldCheck size={10} /> Conversa validada
          </span>
        )}
        {item.conversa?.ok && item.conversa.pessoa && (
          <span title={item.conversa.atendente ? `Atendida por ${item.conversa.atendente}` : ""} style={{ color: "#0f766e", fontWeight: 600 }}>
            Falou com {item.conversa.pessoa}{item.conversa.numero ? ` · ${item.conversa.numero}` : ""}
          </span>
        )}
        {!meu && onAssumir && ["identificado", "abordagem"].includes(item.estagio) && (
          <button onClick={onAssumir} style={{ padding: "2px 8px", borderRadius: 10, border: "1px solid #ddd6fe", background: "#fff", color: "#6d28d9", fontSize: 10, fontWeight: 700, cursor: "pointer" }} title="Esta empresa passa a ser sua">
            Assumir
          </button>
        )}
        {podeTransferir && usuarios.length > 0 && (
          <select
            value="" onChange={(e) => { if (e.target.value) onTransferir(e.target.value); }}
            title="Transferir para outra pessoa do comercial"
            style={{ marginLeft: "auto", padding: "2px 6px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 10, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
          >
            <option value="">Transferir…</option>
            {usuarios.filter((u) => u.id !== item.dono).map((u) => <option key={u.id} value={u.id}>{u.full_name || "sem nome"}</option>)}
          </select>
        )}
      </div>

      {/* Sem telefone não há contato possível — e a maioria dos postos que o
          combustível empurra chega assim. A consulta é por CNPJ e é pública. */}
      {!item.telefone && !item.contato && item.estagio === "identificado" && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, flexWrap: "wrap", padding: "6px 9px", borderRadius: 6, background: "#fffbeb", border: "1px solid #fde68a" }}>
          <span style={{ fontSize: 10, color: "#92400e", lineHeight: 1.4 }}>Sem telefone e sem contato — não dá para abordar assim.</span>
          {(item.cnpj || "").replace(/\D/g, "").length === 14 && item.parceiro && (
            <button onClick={onCompletarPeloCnpj} disabled={buscandoCnpj} style={{ padding: "3px 9px", borderRadius: 5, border: "none", background: "#d97706", color: "#fff", fontSize: 10, fontWeight: 700, cursor: buscandoCnpj ? "wait" : "pointer", whiteSpace: "nowrap" }}>
              {buscandoCnpj ? "Buscando..." : "Buscar pelo CNPJ"}
            </button>
          )}
        </div>
      )}

      {/* A mesma empresa com duas fichas: o registro rápido e a que o
          fornecedor mandou pelo link, sem vínculo entre elas. */}
      {duplicatas.length > 0 && (
        <div style={{ padding: "6px 9px", borderRadius: 6, background: "#fef2f2", border: "1px solid #fecaca", display: "flex", flexDirection: "column", gap: 4 }}>
          {duplicatas.map((d) => (
            <div key={d.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, flexWrap: "wrap" }}>
              <span style={{ fontSize: 10, color: "#991b1b", lineHeight: 1.4 }}>
                <strong>Ficha duplicada:</strong> "{d.nomeFantasia || d.razaoSocial || "sem nome"}" ({({ link: "link", contato: "registro rápido", visita: "visita" })[d.origem] || d.origem}, {d.status})
              </span>
              {podeConferir && (
                <button onClick={() => onJuntar(d)} style={{ padding: "3px 9px", borderRadius: 5, border: "none", background: "#dc2626", color: "#fff", fontSize: 10, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                  Juntar fichas
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Parado em abordagem: o silêncio precisa aparecer no card, senão a
          empresa fica encostada no funil como se fosse conversa viva. */}
      {parado && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, padding: "6px 9px", borderRadius: 6, background: "#fef2f2", border: "1px solid #fecaca" }}>
          <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 10.5, color: "#991b1b", fontWeight: 700 }}>
            <AlertCircle size={11} /> Parado há {diasParado(item.parceiro)} dias sem contato
          </span>
          <button onClick={onJustificar} style={{ padding: "3px 9px", borderRadius: 5, border: "none", background: "#dc2626", color: "#fff", fontSize: 10, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
            Justificar
          </button>
        </div>
      )}
      {!parado && espera && (
        <div style={{ display: "flex", gap: 5, alignItems: "flex-start", padding: "6px 9px", borderRadius: 6, background: "#fffbeb", border: "1px solid #fde68a", fontSize: 10.5, color: "#92400e", lineHeight: 1.4 }}>
          <CalendarClock size={11} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>Aguardando o parceiro: {rotuloMotivo(espera.motivo)}{espera.proximaTentativa ? ` · tenta de novo em ${fmtShort(espera.proximaTentativa)}` : ""}</span>
        </div>
      )}

      {/* O que já aconteceu — o card conta a história, não só o rótulo */}
      {item.ultimoContato && item.estagio === "abordagem" && (
        <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 9px", borderRadius: 6, background: "#eef2ff", border: "1px solid #c7d2fe", fontSize: 10.5, color: "#4338ca", fontWeight: 600 }}>
          <CheckCircle2 size={11} /> Contato registrado em {fmtShort(item.ultimoContato)}
          {prova
            ? <span style={{ display: "flex", alignItems: "center", gap: 3, color: "#0f766e" }}><Paperclip size={10} /> com print</span>
            : <span style={{ color: "#b45309" }}>· sem comprovante</span>}
        </div>
      )}
      {st && (
        <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 10.5, color: "#475569" }}>
          <FileText size={11} color="#7c3aed" /> Ficha: <Badge color={st.color} small>{st.label}</Badge>
          {st.docsPend?.length > 0 && <span style={{ color: "#1d4ed8" }}>· falta {st.docsPend.map((d) => d.label).join(", ")}</span>}
        </div>
      )}
      {item.estagio === "sem_interesse" && (
        <div style={{ display: "flex", gap: 6, alignItems: "flex-start", padding: "6px 9px", borderRadius: 6, background: "#fffbeb", border: "1px solid #fde68a", fontSize: 10.5, color: "#92400e", lineHeight: 1.4 }}>
          <CalendarClock size={11} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{item.motivoSemInteresse || "Sem motivo registrado"}{item.retomarEm ? ` · retomar em ${fmtShort(item.retomarEm)}` : ""}</span>
        </div>
      )}
      {/* O gerente pediu para refazer: quem executa precisa ver isso no card,
          não enterrado no histórico. */}
      {correcao && (
        <div style={{ display: "flex", gap: 5, alignItems: "flex-start", padding: "6px 9px", borderRadius: 6, background: "#fff7ed", border: "1px solid #fed7aa", fontSize: 10.5, color: "#9a3412", lineHeight: 1.4 }}>
          <RotateCcw size={11} style={{ flexShrink: 0, marginTop: 1 }} />
          <span><strong>Gerência pediu para refazer:</strong> {correcao.comentario || "ver histórico"}</span>
        </div>
      )}
      {qualidade.total > 0 && qualidade.fraco > 0 && (
        <div style={{ fontSize: 10, color: "#b45309" }}>
          {qualidade.fraco} de {qualidade.total} {qualidade.total === 1 ? "registro avaliado" : "registros avaliados"} como "precisa melhorar"
        </div>
      )}
      {/* O que falta para a empresa andar sozinha. Sem isto, "Documentação"
          seria uma sala de espera sem placa: ninguém sabe o que trava. */}
      {item.aguardandoFicha && (
        <div style={{ fontSize: 10, color: "#6d28d9", background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: 6, padding: "4px 8px" }}>
          Aceitou, mas a ficha ainda não chegou. {item.ficha
            ? <>Abra a ficha e mande o link — ele reabre <strong>esta mesma ficha</strong>.</>
            : <>Mande o <strong>formulário</strong> pelo botão abaixo.</>} Vai para Documentação sozinha quando o fornecedor enviar.
        </div>
      )}
      {item.estagio === "documentacao" && !item.aguardandoFicha && ev.temFicha && !ev.assinada && (
        <div style={{ fontSize: 10, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "4px 8px" }}>
          Vai para Validação sozinha quando a ficha for <strong>concluída</strong> (assinada). Os documentos da empresa são cobrados depois pelo link — não travam o cadastro.
        </div>
      )}
      {item.estagio === "validacao" && (
        <div style={{ fontSize: 10, color: "#1d4ed8", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 6, padding: "4px 8px" }}>
          Assinada — vira <strong>Ativo</strong> sozinha quando o fornecedor estiver cadastrado no módulo (cadastro + vínculo). Os documentos são coletados depois, dentro do módulo.
        </div>
      )}
      {item.estagio === "ativacao" && (
        <div style={{ fontSize: 10, color: "#0f766e", background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: 6, padding: "4px 8px" }}>
          Vira <strong>Ativo</strong> sozinha assim que o fornecedor estiver cadastrado no módulo.
        </div>
      )}
      {/* Nasceu na ficha e nunca recebeu contato registrado. Não mexemos no
          estágio sozinhos — quem diz onde a empresa está é quem trabalha nela. */}
      {(semParceiro || semContato) && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, flexWrap: "wrap", padding: "6px 9px", borderRadius: 6, background: "#f5f3ff", border: "1px solid #ddd6fe" }}>
          <span style={{ fontSize: 10, color: "#6d28d9", lineHeight: 1.4 }}>
            {jaAssinou
              ? "Assinou a ficha, mas não há conversa registrada. Se tiver o export do WhatsApp, registre o contato — o estágio não muda."
              : semParceiro
                ? "Veio da ficha — ainda não está no funil da rede."
                : "Sem contato registrado — o estágio veio da ficha, não de uma conversa."}
          </span>
          <span style={{ display: "flex", gap: 4 }}>
            {/* "A contatar" em quem já assinou devolvia o fornecedor ao começo
                do funil. Só vale enquanto a ficha não foi enviada. */}
            {!jaAssinou && <button onClick={() => onTrazerParaFunil("identificado")} style={{ padding: "3px 8px", borderRadius: 5, border: "1px solid #ddd6fe", background: "#fff", color: "#6d28d9", fontSize: 10, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
              A contatar
            </button>}
            {/* "Em conversa" movia direto para abordagem, sem prova. Agora é o
                registro de contato: com o export (ou o número da ligação) a
                empresa anda sozinha para "Em conversa". */}
            <button onClick={onContato} style={{ padding: "3px 8px", borderRadius: 5, border: "none", background: "#7c3aed", color: "#fff", fontSize: 10, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
              Registrar contato
            </button>
          </span>
        </div>
      )}

      {/* Ações do estágio */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", paddingTop: 8, borderTop: "1px solid #f1f5f9" }}>
        {/* Vale também para o card que só tem ficha: ele cria o parceiro e já
            abre o registro. Antes, quem viesse do registro rápido ou do posto
            candidato não tinha por onde lançar a conversa. */}
        {(acoes.includes("contato") || semContato || (!item.parceiro && item.estagio === "identificado")) && (
          <button onClick={onContato} style={btnPri}><Phone size={12} /> Registrar contato</button>
        )}
        {acoes.includes("aceitou") && (
          <button onClick={onAceitou} style={{ ...btnPri, background: "#0d9488" }}><CheckCircle2 size={12} /> Aceitou</button>
        )}
        {acoes.includes("novo_contato") && item.parceiro && (
          <button onClick={onContato} style={btnGhost} title="Registrar mais um contato nesta empresa"><Phone size={12} /></button>
        )}
        {/* A ficha aparece em QUALQUER estágio. Amarrá-la ao estágio deixava a
            empresa em abordagem sem jeito de abrir a ficha que ela já tinha —
            era o contrário da unificação. */}
        {item.ficha
          ? (
            emDocumentacao
              ? <button onClick={onFicha} style={{ ...btnPri, background: "#f59e0b" }} title="A partir daqui o trabalho é a documentação">
                  <FileText size={12} /> Continuar na ficha <ArrowRight size={11} />
                </button>
              : <button onClick={onFicha} style={btnGhost}><FileText size={12} /> Ficha</button>
          )
          : <button onClick={onFormulario} style={btnGhost}><Send size={12} /> Enviar formulário</button>}
        {item.ficha && st?.docsPend?.length > 0 && (
          <button onClick={onDocumentos} style={{ ...btnGhost, color: "#1d4ed8", borderColor: "#bfdbfe" }}><Paperclip size={12} /> Pedir documentos</button>
        )}
        {acoes.includes("sem_interesse") && (
          <button onClick={() => onAbrirRecusa("sem_interesse")} style={{ ...btnGhost, color: "#b45309", borderColor: "#fcd34d" }}><Ban size={12} /> Sem interesse</button>
        )}

        {/* Fim do funil: quem decide daqui para a frente é a gerência. O
            comercial não vê estes botões. */}
        {podeConferir && atos.map((ato) => (
          <button
            key={ato.id}
            onClick={() => (ato.precisaNota ? onAbrirRecusa(ato) : onAtoGerente(ato))}
            style={ato.id === "reprovar"
              ? { ...btnGhost, color: "#b91c1c", borderColor: "#fecaca" }
              : { ...btnPri, background: ato.cor }}
          >
            {ato.id === "reprovar" ? <Ban size={12} /> : <CheckCircle2 size={12} />} {ato.label}
          </button>
        ))}
        {podeConferir && aConferir > 0 && (
          <button onClick={onConferir} style={{ ...btnGhost, color: "#6d28d9", borderColor: "#ddd6fe", background: "#f5f3ff" }} title="Conferir e avaliar o que o comercial registrou">
            <ClipboardCheck size={12} /> Conferir ({aConferir})
          </button>
        )}
        {acoes.includes("retomar") && (
          <button onClick={onRetomar} style={{ ...btnGhost, color: "#6d28d9", borderColor: "#ddd6fe" }}><RotateCcw size={12} /> Retomar</button>
        )}
        <button onClick={() => wa && window.open(wa, "_blank")} disabled={!wa} style={{ ...btnGhost, marginLeft: "auto", color: wa ? "#059669" : "#cbd5e1", borderColor: wa ? "#a7f3d0" : "#e2e8f0" }} title={wa ? "WhatsApp" : "Sem telefone"}>
          <MessageCircle size={12} />
        </button>
        {item.parceiro && <button onClick={onDetalhe} style={btnGhost} title="Abrir detalhe"><Eye size={12} /></button>}
        {/* Só admin: limpar duplicidade. Fica separado das ações do fluxo para
            não virar mais um passo do funil. */}
        {podeExcluir && (
          <button onClick={onExcluir} style={{ ...btnGhost, color: "#dc2626", borderColor: "#fecaca" }} title="Excluir da rede (duplicidade)"><Trash2 size={12} /></button>
        )}
      </div>

      {/* Recusa — três casos, um painel. O motivo é obrigatório nos três.
          "Sem interesse" volta para a fila na data escolhida; "definitivo" e a
          reprovação do gerente saem do funil e não voltam sozinhos. */}
      {aberto && (() => {
        const doGerente = modoRecusa && typeof modoRecusa === "object";
        const definitivo = doGerente || modoRecusa === "definitivo";
        return (
          <div style={{ padding: "10px 12px", borderRadius: 8, background: definitivo ? "#fef2f2" : "#fffbeb", border: `1px solid ${definitivo ? "#fecaca" : "#fde68a"}` }}>
            <div style={{ fontSize: 11.5, fontWeight: 700, color: definitivo ? "#991b1b" : "#92400e", marginBottom: 6 }}>
              {doGerente ? `${modoRecusa.label} — o que motivou?` : "Por que não seguiu?"}
            </div>

            {!doGerente && (
              <div style={{ display: "flex", gap: 5, marginBottom: 8 }}>
                {[
                  { id: "sem_interesse", label: "Agora não — retomar depois" },
                  { id: "definitivo", label: "Definitivo — não retomar" },
                ].map((op) => (
                  <button
                    key={op.id}
                    onClick={() => setModoRecusa(op.id)}
                    style={{
                      flex: 1, padding: "5px 8px", borderRadius: 6, fontSize: 10.5, fontWeight: 600, cursor: "pointer",
                      border: `1px solid ${modoRecusa === op.id ? (op.id === "definitivo" ? "#dc2626" : "#d97706") : "#e2e8f0"}`,
                      background: modoRecusa === op.id ? (op.id === "definitivo" ? "#fee2e2" : "#fef3c7") : "#fff",
                      color: modoRecusa === op.id ? (op.id === "definitivo" ? "#991b1b" : "#92400e") : "#64748b",
                    }}
                  >
                    {op.label}
                  </button>
                ))}
              </div>
            )}

            <input
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder={definitivo ? "Ex: documentação reprovada, desistiu do credenciamento, encerrou atividade" : "Ex: já atende por outra rede, achou a taxa alta, vai avaliar em 2026"}
              style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: `1px solid ${erroMotivo ? "#ef4444" : (definitivo ? "#fecaca" : "#fcd34d")}`, fontSize: 12, boxSizing: "border-box", background: "#fff", color: "#334155" }}
            />
            {erroMotivo && <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10.5, color: "#dc2626", marginTop: 4 }}><AlertCircle size={11} /> {erroMotivo}</div>}

            {!definitivo && (
              <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", margin: "8px 0" }}>
                <span style={{ fontSize: 10.5, color: "#92400e" }}>Retomar em:</span>
                <input type="date" value={retomarEm} onChange={(e) => setRetomarEm(e.target.value)} style={{ padding: "6px 9px", borderRadius: 6, border: "1px solid #fcd34d", fontSize: 11, background: "#fff", color: "#334155" }} />
                {[1, 3, 6].map((m) => (
                  <button key={m} onClick={() => setRetomarEm(addMonths(getToday(), m))} style={{ padding: "4px 9px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontSize: 10.5, fontWeight: 600, cursor: "pointer" }}>{m}m</button>
                ))}
              </div>
            )}
            {definitivo && (
              <div style={{ fontSize: 10, color: "#991b1b", margin: "8px 0", lineHeight: 1.4 }}>
                Sai do funil e <strong>não volta sozinho</strong>. Continua na lista, e dá para retomar à mão se a empresa mudar de ideia.
              </div>
            )}

            <div style={{ display: "flex", gap: 6, marginTop: definitivo ? 0 : 4 }}>
              <button onClick={onConfirmarRecusa} style={{ padding: "6px 13px", borderRadius: 6, border: "none", background: definitivo ? "#dc2626" : "#d97706", color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Confirmar</button>
              <button onClick={onCancelarRecusa} style={{ padding: "6px 13px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Cancelar</button>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function Lbl({ children }) {
  return <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>{children}</div>;
}

const btnPri = { display: "flex", alignItems: "center", gap: 5, padding: "7px 13px", borderRadius: 7, border: "none", background: "#7c3aed", color: "#fff", fontSize: 11.5, fontWeight: 600, cursor: "pointer" };
const btnGhost = { display: "flex", alignItems: "center", gap: 5, padding: "6px 11px", borderRadius: 7, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontSize: 11.5, fontWeight: 600, cursor: "pointer" };
const btnSec = { display: "flex", alignItems: "center", gap: 5, padding: "7px 14px", borderRadius: 7, border: "1px solid #d1d5db", background: "#fff", color: "#475569", fontSize: 12, fontWeight: 600, cursor: "pointer" };
